import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { UserRole } from '@/constants/room'
import OGEAvatar from '@/components/OGE/OGEAvatar'
import { Badge } from '@/components/ui/badge'
import { SERVER_URL } from '@/api/config'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import NegotiationMessagesSkeleton from '@/components/Room/NegotiationMessagesSkeleton'
import { useAppDispatch } from '@/redux/hooks'
import { loadMoreMessages } from '@/redux/asyncThunks/negotiationWebsocket'
import {
  loadMoreTeamChatMessages,
  loadTeamChatMessages,
} from '@/redux/asyncThunks/teamChat'
import {
  castVote,
  closeVoting,
  loadProposals,
  proposeStrategy,
} from '@/redux/asyncThunks/teamDecision'
import { clearTeamDecisionState } from '@/redux/slices/teamDecisionSlice'
import { formatDateRelative, formatTime } from '@/utils/dateTimeUtils'
import { cn } from '@/lib/utils'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { ActiveTab } from '@/constants/chat'
import { Reply, UserPlus, Users } from 'lucide-react'
import { setReplyTarget } from '@/redux/slices/negotiationWebsocketSlice'
import { NegotiationType } from '@/redux/types/negotiationWebsocket'
import GroupChatModal from '@/components/Student/GroupChatModal'
import { isInGroupChat, isInvitedToGroupChat } from '@/utils/groupChatMembers'
import { useSearchParams } from 'react-router-dom'
import { useGamePhase } from '@/hooks/useGamePhase'
import { useTeamCommunicator } from '@/hooks/useTeamCommunicator'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'

type NegotiationMessagesProps = {
  activeTab: string
}

const NegotiationMessages: React.FC<NegotiationMessagesProps> = ({
  activeTab,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const previousTabRef = useRef<string | null>(null)
  const skipNextAutoScrollRef = useRef<boolean>(false)
  const dispatch = useAppDispatch()
  const { teams } = useSelector((state: RootState) => state.team)
  const { user } = useEffectiveUser()
  const roomPlayers = useSelector(selectRoomPlayers)
  const [searchParams] = useSearchParams()
  const { isNegotiationPhase, isDecisionPhase, roundId, roundStatus } =
    useGamePhase()
  const { isTeamCommunicator, isTeamMode } = useTeamCommunicator()

  const isTeamRoom = activeTab === ActiveTab.TeamRoom
  const teamIdFromURL = searchParams.get('teamId')

  const negotiationState = useSelector(
    (state: RootState) => state.negotiationWS
  )
  const teamChatState = useSelector((state: RootState) => state.teamChat)
  const {
    proposals,
    voteCounts,
    myVoteProposalId,
    votingClosed,
    winningProposalId,
    winningStrategyText,
  } = useSelector((state: RootState) => state.teamDecision)

  const currentState = isTeamRoom ? teamChatState : negotiationState

  const [currentPage, setCurrentPage] = useState(1)
  const [isUserNearBottom, setIsUserNearBottom] = useState<boolean>(true)
  const [addMembersOpen, setAddMembersOpen] = useState(false)

  const activeNegotiation = useMemo(
    () =>
      isTeamRoom
        ? undefined
        : negotiationState.negotiations.find(
            (negotiation) =>
              String(negotiation.negotiationId) === String(activeTab)
          ),
    [isTeamRoom, negotiationState.negotiations, activeTab]
  )
  const isGroupChat =
    activeNegotiation?.negotiation_type === NegotiationType.GROUP_ROOM
  const [strategyText, setStrategyText] = useState<string>('')
  const [editingProposalId, setEditingProposalId] = useState<number | null>(
    null
  )
  const [editDraftText, setEditDraftText] = useState<string>('')

  const myProposal = useMemo(() => {
    if (!user?.id) return null
    return (
      proposals.find(
        (proposal) => proposal.proposed_by.member_id === user.id
      ) || null
    )
  }, [proposals, user?.id])

  const orderedProposals = useMemo(() => {
    if (!user?.id) return proposals
    const mine = proposals.find((p) => p.proposed_by.member_id === user.id)
    if (!mine) return proposals
    const rest = proposals.filter((p) => p.id !== mine.id)
    return [mine, ...rest]
  }, [proposals, user?.id])

  const filteredMessages = useMemo(() => {
    if (isTeamRoom) {
      if (user?.role === UserRole.Professor && teamIdFromURL) {
        const targetTeamId = parseInt(teamIdFromURL)
        const teamMessages = teamChatState.messages.filter(
          (msg) =>
            msg.team_id === targetTeamId || Number(msg.team_id) === targetTeamId
        )
        return teamMessages
      }
      return teamChatState.messages
    }
    return negotiationState.messages.filter(
      (msg) => msg.negotiationId == activeTab
    )
  }, [
    isTeamRoom,
    teamChatState.messages,
    negotiationState.messages,
    activeTab,
    user?.role,
    teamIdFromURL,
  ])

  const scrollToBottom = () => {
    messagesContainerRef.current?.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }

  const checkIfNearBottom = () => {
    if (!messagesContainerRef.current) return true
    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current
    return scrollHeight - scrollTop - clientHeight < 100
  }

  useEffect(() => {
    if (
      isTeamRoom &&
      teamChatState.messages.length === 0 &&
      !teamChatState.isLoading
    ) {
      dispatch(loadTeamChatMessages())
    }
  }, [
    isTeamRoom,
    teamChatState.messages.length,
    teamChatState.isLoading,
    dispatch,
  ])

  useEffect(() => {
    if (!isTeamRoom || !isTeamMode) return
    dispatch(loadProposals({ gameRoundId: roundId || undefined }))
  }, [
    dispatch,
    isTeamRoom,
    isTeamMode,
    isDecisionPhase,
    isNegotiationPhase,
    roundId,
  ])

  const previousRoundIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (!isTeamRoom || !isTeamMode) return
    if (
      previousRoundIdRef.current !== null &&
      previousRoundIdRef.current !== roundId
    ) {
      dispatch(clearTeamDecisionState())
      dispatch(loadProposals({ gameRoundId: roundId ?? undefined }))
    }
    previousRoundIdRef.current = roundId ?? null
  }, [dispatch, isTeamRoom, isTeamMode, roundId])

  useEffect(() => {
    if (!isTeamRoom || !isTeamMode) return
    if (roundStatus === ActivityState.Completed) {
      dispatch(clearTeamDecisionState())
    }
  }, [dispatch, isTeamRoom, isTeamMode, roundStatus])

  useEffect(() => {
    const switchedToTeamRoom =
      activeTab === ActiveTab.TeamRoom &&
      previousTabRef.current !== ActiveTab.TeamRoom

    if (switchedToTeamRoom) {
      skipNextAutoScrollRef.current = true
      setIsUserNearBottom(false)
      messagesContainerRef.current?.scrollTo({
        top: 0,
        behavior: 'auto',
      })
    }

    previousTabRef.current = activeTab
  }, [activeTab])

  useEffect(() => {
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false
      return
    }

    if (isUserNearBottom && !currentState.loadingMoreMessages) {
      setTimeout(scrollToBottom, 100)
    }
  }, [
    activeTab,
    filteredMessages,
    isUserNearBottom,
    currentState.loadingMoreMessages,
  ])

  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current || currentState.loadingMoreMessages)
      return

    const container = messagesContainerRef.current
    setIsUserNearBottom(checkIfNearBottom())

    if (container.scrollTop < 100 && currentState.pagination?.has_next) {
      const nextPage = currentPage + 1
      const scrollHeight = container.scrollHeight

      const loadAction = isTeamRoom
        ? loadMoreTeamChatMessages({ page: nextPage, pageSize: 20 })
        : loadMoreMessages({
            page: nextPage,
            pageSize: 20,
            negotiationId: activeTab,
          })

      dispatch(loadAction).then(() => {
        setTimeout(() => {
          if (messagesContainerRef.current) {
            const scrollDiff =
              messagesContainerRef.current.scrollHeight - scrollHeight
            messagesContainerRef.current.scrollTop =
              container.scrollTop + scrollDiff
          }
        }, 100)
      })

      setCurrentPage(nextPage)
    }
  }, [
    currentState.loadingMoreMessages,
    currentState.pagination?.has_next,
    currentPage,
    dispatch,
    isTeamRoom,
    activeTab,
  ])

  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    let timeoutId: NodeJS.Timeout
    const throttledScroll = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(handleScroll, 150)
    }

    container.addEventListener('scroll', throttledScroll)
    return () => {
      container.removeEventListener('scroll', throttledScroll)
      clearTimeout(timeoutId)
    }
  }, [handleScroll])

  const getPlayerTeam = useCallback(
    (email: string) => {
      const player = roomPlayers.find((p) => p.member_email === email)
      const team = teams.find((t) => t.id === player?.teamId)
      return team?.name || `Team ${player?.teamId}`
    },
    [roomPlayers, teams]
  )

  if (currentState.isLoading && filteredMessages.length === 0) {
    return (
      <div
        ref={messagesContainerRef}
        className='overflow-auto flex-1 [&::-webkit-scrollbar]:hidden'
      >
        <NegotiationMessagesSkeleton />
      </div>
    )
  }

  return (
    <div
      ref={messagesContainerRef}
      className='overflow-auto flex-1 [&::-webkit-scrollbar]:hidden'
    >
      {isGroupChat && (
        <div className='sticky top-0 z-10 mx-4 mt-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white/95 px-3 py-2 shadow-sm backdrop-blur-sm'>
          <div className='flex items-center gap-2 text-sm text-gray-700'>
            <Users className='h-4 w-4 text-gray-500' />
            <span>
              {(activeNegotiation?.members || []).filter(isInGroupChat).length}{' '}
              members
            </span>
          </div>
          {user?.role !== UserRole.Professor && (
            <button
              type='button'
              onClick={() => setAddMembersOpen(true)}
              className='flex items-center gap-1.5 rounded-md border border-dark-blue px-2.5 py-1 text-xs font-medium text-dark-blue hover:bg-blue-50'
            >
              <UserPlus className='h-3.5 w-3.5' />
              Add members
            </button>
          )}
          <GroupChatModal
            isOpen={addMembersOpen}
            onClose={() => setAddMembersOpen(false)}
            mode='add'
            negotiationId={String(activeTab)}
            existingEmails={(activeNegotiation?.members || [])
              .filter(isInGroupChat)
              .map((member) => member.email)}
            invitedEmails={(activeNegotiation?.members || [])
              .filter(isInvitedToGroupChat)
              .map((member) => member.email)}
          />
        </div>
      )}

      {isTeamRoom && isTeamMode && (
        <div className='p-4 mx-4 mt-4 mb-3 bg-white rounded-lg border border-gray-200'>
          <div className='mb-3'>
            <h3 className='text-sm font-semibold text-dark-blue'>
              Proposed Strategies
            </h3>
            <p className='text-xs text-gray-500'>
              Discuss in chat, then propose, vote, and close in the correct
              phase.
            </p>
          </div>

          {isNegotiationPhase && (
            <div className='mb-4 space-y-2'>
              {!myProposal && (
                <>
                  <textarea
                    value={strategyText}
                    onChange={(e) => setStrategyText(e.target.value)}
                    placeholder='Propose your team strategy...'
                    className='w-full min-h-[84px] rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                  />
                  <div className='flex justify-end'>
                    <button
                      type='button'
                      onClick={() => {
                        if (!strategyText.trim()) return
                        dispatch(
                          proposeStrategy({ strategyText: strategyText.trim() })
                        )
                        setStrategyText('')
                      }}
                      disabled={!strategyText.trim()}
                      className='rounded-md bg-dark-blue px-3 py-1.5 text-xs font-medium text-white disabled:bg-gray-300'
                    >
                      Propose strategy
                    </button>
                  </div>
                </>
              )}
              {myProposal && (
                <p className='text-[11px] text-gray-500'>
                  You already submitted a strategy. Use the Edit option on your
                  strategy card below to update it during negotiation.
                </p>
              )}
            </div>
          )}

          <div className='space-y-2'>
            {proposals.length === 0 ? (
              <p className='text-sm text-gray-500'>
                No strategies proposed yet.
              </p>
            ) : (
              orderedProposals.map((proposal) => {
                const votes = voteCounts[proposal.id] || 0
                const isMine = myVoteProposalId === proposal.id
                const isWinner =
                  votingClosed && winningProposalId === proposal.id
                const isMyProposal = proposal.proposed_by.member_id === user?.id
                const isEditingThisProposal = editingProposalId === proposal.id
                return (
                  <div
                    key={proposal.id}
                    className={cn(
                      'p-3 rounded-md border',
                      isWinner
                        ? 'bg-green-50 border-green-400'
                        : 'bg-gray-50 border-gray-200'
                    )}
                  >
                    <div className='flex gap-2 justify-between items-center mb-1'>
                      <p className='text-xs text-gray-600'>
                        {proposal.proposed_by.name} (
                        {proposal.proposed_by.email})
                      </p>
                      <div className='flex gap-2 items-center'>
                        <p className='text-xs text-gray-500'>
                          {formatTime(proposal.created_at)}
                        </p>
                        {isNegotiationPhase &&
                          isMyProposal &&
                          !isEditingThisProposal && (
                            <button
                              type='button'
                              onClick={() => {
                                setEditingProposalId(proposal.id)
                                setEditDraftText(proposal.strategy_text)
                              }}
                              className='rounded border border-blue-700 px-2 py-0.5 text-[11px] font-medium text-blue-800 hover:bg-blue-100'
                            >
                              Edit
                            </button>
                          )}
                      </div>
                    </div>
                    {isEditingThisProposal ? (
                      <div className='space-y-2'>
                        <textarea
                          value={editDraftText}
                          onChange={(e) => setEditDraftText(e.target.value)}
                          className='w-full min-h-[84px] rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                        />
                        <div className='flex gap-2 justify-end'>
                          <button
                            type='button'
                            onClick={() => {
                              setEditingProposalId(null)
                              setEditDraftText('')
                            }}
                            className='px-3 py-1 text-xs font-medium text-gray-700 rounded border border-gray-300 hover:bg-gray-100'
                          >
                            Cancel
                          </button>
                          <button
                            type='button'
                            onClick={() => {
                              if (!editDraftText.trim()) return
                              dispatch(
                                proposeStrategy({
                                  strategyText: editDraftText.trim(),
                                })
                              )
                              setEditingProposalId(null)
                              setEditDraftText('')
                            }}
                            disabled={!editDraftText.trim()}
                            className='px-3 py-1 text-xs font-medium text-white rounded bg-dark-blue disabled:bg-gray-300'
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className='text-sm text-gray-900 whitespace-pre-wrap'>
                        {proposal.strategy_text}
                      </p>
                    )}

                    {isDecisionPhase && (
                      <div className='flex justify-between items-center mt-3'>
                        <p className='text-xs font-medium text-gray-700'>
                          Votes: {votes}
                          {isMine ? ' (your vote)' : ''}
                        </p>
                        {!votingClosed && (
                          <button
                            type='button'
                            onClick={() =>
                              dispatch(castVote({ proposalId: proposal.id }))
                            }
                            className='rounded border border-dark-blue px-2.5 py-1 text-xs font-medium text-dark-blue hover:bg-blue-50'
                          >
                            Vote
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>

          {isDecisionPhase && (
            <div className='flex justify-between items-center mt-4'>
              <p className='text-xs text-gray-600'>
                {votingClosed
                  ? winningStrategyText
                    ? 'Voting closed. Winner selected.'
                    : 'Voting closed. No single winner.'
                  : 'Voting is open.'}
              </p>
              {isTeamCommunicator && !votingClosed && (
                <button
                  type='button'
                  onClick={() => dispatch(closeVoting())}
                  className='rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700'
                >
                  Close voting
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {currentState.loadingMoreMessages && (
        <div className='flex flex-col justify-center items-center py-8 mx-4 rounded-lg border-2 border-gray-300 border-dashed bg-gray-50/50'>
          <div className='mb-3 w-12 h-12 rounded-full border-4 border-blue-500 animate-spin border-t-transparent' />
          <p className='font-medium text-gray-600'>Loading more messages...</p>
        </div>
      )}

      {currentState.pagination?.has_next &&
        !currentState.loadingMoreMessages &&
        filteredMessages.length > 0 && (
          <div className='py-2 text-sm text-center text-gray-500'>
            Scroll up to load more messages
          </div>
        )}

      {filteredMessages.length === 0 && !currentState.isLoading ? (
        <div className='flex justify-center items-center h-full text-gray-500'>
          <p>No messages yet. Start the conversation!</p>
        </div>
      ) : (
        filteredMessages.map((message, idx) => {
          const isRightAligned = message.user === user?.email
          const showDate =
            idx === 0 ||
            (message.entrytime &&
              filteredMessages[idx - 1]?.entrytime &&
              formatDateRelative(message.entrytime) !==
                formatDateRelative(filteredMessages[idx - 1].entrytime!))

          return (
            <div key={message.id || idx}>
              {showDate && message.entrytime && (
                <div className='flex justify-center items-center my-4'>
                  <div className='px-3 py-1 text-xs font-medium text-gray-600 bg-gray-200 rounded-full'>
                    {formatDateRelative(message.entrytime)}
                  </div>
                </div>
              )}

              <div
                className={cn(
                  'group flex mb-4 items-center gap-1.5',
                  isRightAligned ? 'justify-end' : 'justify-start'
                )}
              >
                {!isTeamRoom && isRightAligned && message.id && (
                  <button
                    type='button'
                    title='Reply'
                    aria-label='Reply'
                    onClick={() =>
                      dispatch(
                        setReplyTarget({
                          negotiationId: String(activeTab),
                          target: {
                            id: message.id!,
                            name:
                              message.name?.split('(')[0].trim() ||
                              message.user,
                            excerpt: message.message.slice(0, 120),
                          },
                        })
                      )
                    }
                    className='rounded-full p-1.5 text-gray-400 opacity-0 transition-opacity hover:bg-slate-200 hover:text-gray-600 group-hover:opacity-100'
                  >
                    <Reply className='h-4 w-4' />
                  </button>
                )}
                <div
                  className={cn(
                    'max-w-[80%] p-4 rounded-[20px] bg-[#E6ECF1]',
                    isRightAligned ? 'text-right' : 'text-left'
                  )}
                >
                  {!isRightAligned && (
                    <div className='flex gap-3 items-center mb-2'>
                      <OGEAvatar
                        src={
                          message.user_profile_picture
                            ? SERVER_URL + message.user_profile_picture
                            : null
                        }
                        fallback={message.name?.substring(0, 2).toUpperCase()}
                        variant='sm'
                        isLoading={false}
                      />
                      <div className='flex flex-col'>
                        <span className='text-base font-medium text-dark-blue'>
                          {message.name?.split('(')[0].trim() || message.user}
                          {message.sender_role === UserRole.Professor && (
                            <Badge className='ml-2 pointer-events-none hover:bg-none'>
                              white-cell
                            </Badge>
                          )}
                        </span>
                        {message.sender_role !== UserRole.Professor &&
                          getPlayerTeam(message.user) && (
                            <span className='text-xs text-gray-500'>
                              {getPlayerTeam(message.user)}
                            </span>
                          )}
                      </div>
                    </div>
                  )}
                  {'reply_to' in message && message.reply_to && (
                    <div className='mb-2 rounded-md border-l-2 border-dark-blue bg-white/60 px-2.5 py-1.5 text-left'>
                      <p className='text-xs font-semibold text-dark-blue'>
                        {message.reply_to.name}
                      </p>
                      <p className='truncate text-xs text-gray-600'>
                        {message.reply_to.excerpt}
                      </p>
                    </div>
                  )}
                  <p className='text-sm font-medium text-black whitespace-break-spaces'>
                    {message.message}
                  </p>

                  {message.entrytime && (
                    <div
                      className={cn(
                        'flex mt-2',
                        isRightAligned ? 'justify-end' : 'justify-start'
                      )}
                    >
                      <span className='text-xs text-gray-500 opacity-75'>
                        {formatTime(message.entrytime)}
                      </span>
                    </div>
                  )}
                </div>
                {!isTeamRoom && !isRightAligned && message.id && (
                  <button
                    type='button'
                    title='Reply'
                    aria-label='Reply'
                    onClick={() =>
                      dispatch(
                        setReplyTarget({
                          negotiationId: String(activeTab),
                          target: {
                            id: message.id!,
                            name:
                              message.name?.split('(')[0].trim() ||
                              message.user,
                            excerpt: message.message.slice(0, 120),
                          },
                        })
                      )
                    }
                    className='rounded-full p-1.5 text-gray-400 opacity-0 transition-opacity hover:bg-slate-200 hover:text-gray-600 group-hover:opacity-100'
                  >
                    <Reply className='h-4 w-4' />
                  </button>
                )}
              </div>
            </div>
          )
        })
      )}

      <div ref={messagesEndRef} />
    </div>
  )
}

export default NegotiationMessages
